/**
 * The specialism picker.
 *
 * A category can hold a dozen specialisms, and picking one out of a native
 * menu means reading the whole list every time. This is the same control with
 * a filter on it: type a fragment, the matched run is marked, the arrows move
 * a highlight that never moves the row, and return selects.
 *
 * On a touch device the native `<select>` is still the better control — it
 * gets the platform's own wheel and its own keyboard — so this replaces it
 * only where there is a real pointer.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

export interface ComboOption {
  readonly value: string;
  readonly label: string;
}

/** True on a device with a real pointer, where a panel beats the native menu. */
export function hasFinePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

/** Split a label around the matched run, so the match can be marked. */
export function splitMatch(
  label: string,
  query: string,
): { readonly before: string; readonly hit: string; readonly after: string } {
  if (query === '') return { before: label, hit: '', after: '' };
  const at = label.toLowerCase().indexOf(query.toLowerCase());
  if (at < 0) return { before: label, hit: '', after: '' };
  return {
    before: label.slice(0, at),
    hit: label.slice(at, at + query.length),
    after: label.slice(at + query.length),
  };
}

export function filterOptions(
  options: readonly ComboOption[],
  query: string,
): readonly ComboOption[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return options;
  return options.filter((option) => option.label.toLowerCase().includes(needle));
}

export function Combobox({
  label,
  value,
  options,
  onChange,
  placeholder,
  emptyPlaceholder,
  disabled = false,
  required = false,
  hint,
  error,
  scopeName,
}: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly ComboOption[];
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
  readonly emptyPlaceholder?: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly hint?: ReactNode;
  readonly error?: string | undefined;
  readonly scopeName?: string;
}): ReactNode {
  const id = useId();
  const listId = `${id}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement | null>(null);
  const search = useRef<HTMLInputElement | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);

  const matches = filterOptions(options, query);
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    search.current?.focus();
    const onPointer = (event: MouseEvent): void => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  function close(restoreFocus = true): void {
    setOpen(false);
    setQuery('');
    setActive(0);
    if (restoreFocus) trigger.current?.focus();
  }

  function choose(option: ComboOption | undefined): void {
    if (option === undefined) return;
    onChange(option.value);
    close();
  }

  function keys(event: React.KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (matches.length === 0 ? 0 : (index + 1) % matches.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) =>
        matches.length === 0 ? 0 : (index - 1 + matches.length) % matches.length,
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(matches[active]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      // Tab settles on the active row and carries on to the next control.
      if (matches[active] !== undefined) onChange((matches[active] as ComboOption).value);
      setOpen(false);
      setQuery('');
      setActive(0);
    }
  }

  const classes = ['combo'];
  if (open) classes.push('is-open');
  if (disabled) classes.push('is-disabled');

  return (
    <div className="field">
      <label htmlFor={id} id={`${id}-label`}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <div className={classes.join(' ')} ref={root}>
        <button
          id={id}
          ref={trigger}
          type="button"
          className="combo-trigger"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-invalid={error !== undefined}
          onClick={() => setOpen((was) => !was)}
        >
          <span className={selected === undefined ? 'is-placeholder' : undefined}>
            {selected?.label ?? (disabled ? (emptyPlaceholder ?? placeholder) : placeholder)}
          </span>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
            <path
              d="M1 1l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {open ? (
          <div className="combo-panel" role="presentation">
            <div className="combo-search">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <circle cx="6.2" cy="6.2" r="4.2" stroke="currentColor" strokeWidth="1.4" />
                <path
                  d="M9.4 9.4L12 12"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
              <input
                ref={search}
                type="text"
                value={query}
                autoComplete="off"
                aria-label={`Filter ${label.toLowerCase()}`}
                aria-controls={listId}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActive(0);
                }}
                onKeyDown={keys}
              />
            </div>

            <div className="combo-list" id={listId} role="listbox" aria-labelledby={`${id}-label`}>
              {matches.length === 0 ? (
                <p className="combo-empty">Nothing matches that.</p>
              ) : (
                matches.map((option, index) => {
                  const parts = splitMatch(option.label, query.trim());
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={option.value === value}
                      className={index === active ? 'combo-row is-active' : 'combo-row'}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => choose(option)}
                    >
                      <span>
                        {parts.before}
                        {parts.hit === '' ? null : <mark>{parts.hit}</mark>}
                        {parts.after}
                      </span>
                      {index === active ? (
                        <span className="mono combo-enter" aria-hidden="true">
                          ↵
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>

            <div className="combo-foot">
              <span>
                {matches.length} of {options.length}
                {scopeName === undefined ? '' : ` in ${scopeName}`}
              </span>
              <span className="mono" aria-hidden="true">
                ↑↓ move · esc close
              </span>
            </div>
          </div>
        ) : null}
      </div>
      {hint === undefined ? null : <span className="hint">{hint}</span>}
      {error === undefined ? null : <span className="error">{error}</span>}
    </div>
  );
}
